import {
    SortInfo,
    type DataType as JhaDataType,
} from '@banno/jha-wc/src/tables/advanced/jha-advanced-table/jha-advanced-table.types';
import { exists } from '@jack-henry/frontend-utils/functions';
import { DeepRequired } from '@treasury/utils/types';
import { HTMLTemplateResult } from 'lit';
import type {
    JhaFilterSubset,
    JhaTableColumn,
    JhaTableRowAction,
    JhaTableView,
} from './jha-advanced-table.types';
import {
    TmTableFilterInternal,
    TmTableRowActionInternal,
    TmTableViewInternal,
} from './tm-table-internal.types';
import {
    AllViewId,
    InitialSort,
    NullViewId,
    SingleViewId,
    TmTableColsDef,
    TmTableColumn,
} from './tm-table.types';

/**
 * Map TM table view representations to the shape used by the `<jha-advanced-table>` component.
 *
 * @param items The list of items shown in the current view.
 * This is primarily to enable showing a count in the view tab label.
 */
export function mapViews<T>(views: TmTableViewInternal<T>[]): JhaTableView[] {
    return views
        .map(v => {
            const { viewId: id, label, defaultQuery: searchQuery } = v;
            if (id === AllViewId || id === NullViewId || id === SingleViewId) {
                return undefined;
            }

            return {
                id,
                label,
                config: {
                    searchQuery,
                    sort: mapInitialSort(v.initialSort as InitialSort<T>, v.columns.definitions),
                },
                actions: {},
            };
        })
        .filter(exists);
}

export function mapRowActions<T>(
    rowActions: DeepRequired<Omit<TmTableRowActionInternal<T>, 'execute'>[]> = []
): JhaTableRowAction[] {
    return rowActions.map(action => {
        const { label, isHidden, isDisabled, icon } = action;
        return {
            id: action.actionId,
            icon: icon === '' ? undefined : icon,
            label,
            hiddenCondition:
                typeof isHidden === 'function'
                    ? (item: Record<string, unknown>) => isHidden(item as T)
                    : !!isHidden,
            disabledCondition:
                typeof isDisabled === 'function'
                    ? (item: Record<string, unknown>) => isDisabled(item as T)
                    : !!isDisabled,
        };
    });
}

/**
 * Map a `TmBoColsDef` to the column shape used by the `<jha-advanced-table>` component.
 */
export function mapCols<T>(
    cols: Required<TmTableColsDef<T>>,
    filters: TmTableFilterInternal<T>[] = []
): JhaTableColumn[] {
    const { definitions } = cols;

    if (definitions.length < 1) {
        // return an array containing a dummy column
        // so that the "No columns visible message" doesn't show
        return [{ label: '', id: '' }];
    }

    const jhaFilters = mapFilters(filters);

    return definitions.map(col => {
        const { prop, label = '', sortable: sortBy, searchable, render, alignment, dataType } = col;
        const hasDataType = dataType && dataType !== 'string';
        const column: JhaTableColumn = {
            id: prop.toString(),
            label,
            sortBy,
            searchable,
            alignRight: alignment === 'right',
            valueRenderer: render
                ? (value, item) => {
                      // casting here to support returning Lit's nothing terminal value in our code;
                      // it doesn't break the advanced table and returning an empty string just uses the value
                      return render(value as T[keyof T], item as T) as HTMLTemplateResult;
                  }
                : undefined,
            valueFormatter: value => {
                if (Array.isArray(value)) {
                    return value.join('\n');
                }
                return value as string;
            },
            ...jhaFilters[prop],
        };
        // Set dataType after spread to ensure it's not overridden
        if (hasDataType) {
            column.dataType = dataType as JhaDataType;
        }
        return column;
    });
}

export function mapInitialSort<T>(
    initialSort: InitialSort<T>,
    columns: Pick<TmTableColumn<T, keyof T>, 'prop' | 'sortable'>[]
): SortInfo | undefined {
    if (columns.length < 1) {
        return undefined;
    }

    if (initialSort) {
        return {
            columnId: initialSort.prop as string,
            descending: initialSort.direction === 'desc',
        };
    }

    const firstSortable = columns.find(c => c.sortable);
    if (firstSortable) {
        return {
            columnId: firstSortable.prop as string,
            descending: false,
        };
    }
    return undefined;
}

function mapFilters<T>(filters: TmTableFilterInternal<T>[]): Record<keyof T, JhaFilterSubset> {
    return filters.reduce(
        (filterDict, f) => {
            const { prop, type: filterType, choices: options } = f;

            filterDict[prop] = {
                filterType,
                options,
            };

            return filterDict;
        },
        {} as Record<keyof T, JhaFilterSubset>
    );
}
